import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdGavel,
  MdBusiness,
  MdPerson,
  MdInfo,
  MdDescription,
  MdAttachMoney,
  MdSchedule,
  MdQuestionAnswer,
  MdMessage,
  MdPictureAsPdf,
  MdEdit,
  MdFlight,
  MdSettings,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

// ─── Enum maps ────────────────────────────────────────────────────────────────
const TENDER_STATUS: Record<number, { label: string; color: string; bg: string }> = {
  0: { label: "Draft",            color: "#6B7280", bg: "rgba(107,114,128,.15)" },
  1: { label: "Pending Approval", color: "#F59E0B", bg: "rgba(245,158,11,.15)"  },
  2: { label: "Active",           color: "#16A34A", bg: "rgba(22,163,74,.15)"   },
  3: { label: "Closed",           color: "#F97316", bg: "rgba(249,115,22,.15)"  },
  4: { label: "Closed by Issuer", color: "#F97316", bg: "rgba(249,115,22,.15)"  },
  5: { label: "Pending Award",    color: "#EAB308", bg: "rgba(234,179,8,.15)"   },
  6: { label: "Awarded",          color: "#7C3AED", bg: "rgba(124,58,237,.15)"  },
  7: { label: "Cancelled",        color: "#DC2626", bg: "rgba(220,38,38,.15)"   },
};

const BID_STATUS: Record<number, { label: string; color: string }> = {
  1: { label: "Active",     color: "#16A34A" },
  2: { label: "Withdrawn",  color: "#DC2626" },
  3: { label: "Superseded", color: "#6B7280" },
};

const CLARIFICATION_STATUS: Record<number, { label: string; color: string }> = {
  1: { label: "Pending",    color: "#EAB308" },
  2: { label: "Responded",  color: "#16A34A" },
  3: { label: "Closed",     color: "#6B7280" },
};

const BIDDING_METHOD: Record<number, string> = { 1: "Open", 2: "Sealed" };

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtMoney = (v?: number | null, cur = "USD") =>
  v != null ? `${cur} ${Number(v).toLocaleString()}` : "—";

// ─── Shared micro-components ─────────────────────────────────────────────────
const Loader = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
    <div
      style={{
        width: 42, height: 42,
        border: "4px solid #CBD5E1", borderTopColor: "#2563EB",
        borderRadius: "50%", animation: "spin 1s linear infinite",
      }}
    />
  </div>
);

const StatusBadge = ({ status }: { status: number }) => {
  const s = TENDER_STATUS[status] ?? TENDER_STATUS[0];
  return <Badge label={s.label} color={s.color} bg={s.bg} />;
};

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div style={{
    display: "grid", gridTemplateColumns: "26px 170px 1fr",
    gap: 10, padding: "6px 0", borderBottom: "1px solid #E5E7EB", fontSize: 12,
  }}>
    <Icon size={18} color="#2563EB" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value ?? "—"}</span>
  </div>
);

const SectionCard = ({ icon: Icon, title, children, color = "#3B82F6" }: any) => (
  <div style={{ border: "1px solid #E5E7EB", borderRadius: 14, padding: 16, background: "#FFFFFFAA" }}>
    <div style={{
      display: "flex", alignItems: "center", gap: 8, marginBottom: 14,
      background: `linear-gradient(135deg,${color} 0%,${color}CC 100%)`,
      color: "#fff", borderRadius: 8, padding: "6px 10px",
    }}>
      <Icon size={18} />
      <strong style={{ fontSize: 13 }}>{title}</strong>
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div style={{
    flex: "1 1 120px", background: "#fff", border: `2px solid ${color}33`,
    borderRadius: 12, padding: "14px 18px", display: "flex", flexDirection: "column", gap: 4,
  }}>
    <span style={{ fontSize: 26, fontWeight: 700, color }}>{value}</span>
    <span style={{ fontSize: 11, color: "#64748B" }}>{label}</span>
  </div>
);

// ─── Status Update Modal ──────────────────────────────────────────────────────
const StatusUpdateModal = ({ row, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [status, setStatus] = useState<number>(row.status);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await api.adminTender.updateStatus(row.id, status);
    setSaving(false);
    onClose();
    onSaved();
  };

  return (
    <Modal open title={`Update Status — ${row.title}`} onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 6 }}>New Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(+e.target.value)}
          style={{ width: "100%", height: 38, borderRadius: 8, border: "1px solid #D1D5DB", padding: "0 10px" }}
        >
          {Object.entries(TENDER_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Button icon={MdCheck} loading={saving} onClick={save}>Save</Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </Modal>
  );
};

// ─── Module Detail Section ────────────────────────────────────────────────────
const ModuleDetailSection = ({ detail, moduleCode }: any) => {
  if (!detail) return null;

  const rows: { label: string; value: any; icon: any }[] = [];

  if (detail.aircraftTypeName)      rows.push({ label: "Aircraft Type",   value: detail.aircraftTypeName,      icon: MdFlight });
  if (detail.aircraftTypeModelName) rows.push({ label: "Aircraft Model",  value: detail.aircraftTypeModelName, icon: MdFlight });
  if (detail.aircraftTypeModelDetail) rows.push({ label: "Model Detail",  value: detail.aircraftTypeModelDetail, icon: MdInfo });
  if (detail.yom)                   rows.push({ label: "Year of Manufacture", value: detail.yom,               icon: MdInfo });
  if (detail.msn)                   rows.push({ label: "MSN",             value: detail.msn,                   icon: MdInfo });
  if (detail.registration)          rows.push({ label: "Registration",    value: detail.registration,          icon: MdInfo });
  if (detail.capacity)              rows.push({ label: "Capacity",        value: detail.capacity,              icon: MdInfo });
  if (detail.typeIsCargo != null)   rows.push({ label: "Cargo Type",      value: detail.typeIsCargo ? "Yes" : "No", icon: MdInfo });
  if (detail.airportName)           rows.push({ label: "Airport",         value: `${detail.airportName}${detail.airportCode ? ` (${detail.airportCode})` : ""}`, icon: MdFlight });
  if (detail.engineTypeName)        rows.push({ label: "Engine Type",     value: detail.engineTypeName,        icon: MdSettings });
  if (detail.engineModelDetail)     rows.push({ label: "Engine Model Detail", value: detail.engineModelDetail, icon: MdSettings });
  if (detail.esn)                   rows.push({ label: "ESN",             value: detail.esn,                   icon: MdSettings });
  if (detail.tradeType)             rows.push({ label: "Trade Type",      value: detail.tradeType,             icon: MdInfo });
  if (detail.condition)             rows.push({ label: "Condition",       value: detail.condition,             icon: MdInfo });
  if (detail.totalFlightHour)       rows.push({ label: "Total Flight Hours", value: detail.totalFlightHour,    icon: MdInfo });
  if (detail.totalFlightCycle)      rows.push({ label: "Total Flight Cycles", value: detail.totalFlightCycle,  icon: MdInfo });
  if (detail.mtow)                  rows.push({ label: "MTOW",            value: detail.mtow,                  icon: MdInfo });
  if (detail.deliveryTerms)         rows.push({ label: "Delivery Terms",  value: detail.deliveryTerms,         icon: MdInfo });
  if (detail.redeliveryTerms)       rows.push({ label: "Redelivery Terms", value: detail.redeliveryTerms,     icon: MdInfo });
  if (detail.ownerName)             rows.push({ label: "Owner",           value: detail.ownerName,             icon: MdBusiness });
  if (detail.aocHolder)             rows.push({ label: "AOC Holder",      value: detail.aocHolder,             icon: MdBusiness });
  if (detail.backUpAircraft != null) rows.push({ label: "Backup Aircraft", value: detail.backUpAircraft ? "Yes" : "No", icon: MdFlight });
  if (detail.seatOfCrew)            rows.push({ label: "Seat of Crew",    value: detail.seatOfCrew,            icon: MdPerson });
  if (detail.tslsv)                 rows.push({ label: "TSLSV",           value: detail.tslsv,                 icon: MdSettings });
  if (detail.cslsv)                 rows.push({ label: "CSLSV",           value: detail.cslsv,                 icon: MdSettings });
  if (detail.engineFirstLimiterCycle) rows.push({ label: "Engine First Limiter Cycle", value: detail.engineFirstLimiterCycle, icon: MdSettings });
  if (detail.engineStand != null)   rows.push({ label: "Engine Stand",    value: detail.engineStand ? "Yes" : "No", icon: MdSettings });
  if (detail.mmBlockHourGuarantee)  rows.push({ label: "MM Block Hour Guarantee", value: detail.mmBlockHourGuarantee, icon: MdSettings });
  if (detail.flightRange)           rows.push({ label: "Flight Range",    value: detail.flightRange,           icon: MdFlight });

  if (rows.length === 0) return null;

  return (
    <SectionCard icon={MdFlight} title={`Module Details — ${moduleCode}`} color="#0EA5E9">
      {rows.map((r, i) => <InfoRow key={i} icon={r.icon} label={r.label} value={r.value} />)}
    </SectionCard>
  );
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const TenderDetailModal = ({ id, onClose }: { id: number; onClose: () => void }) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminTender.getById(id).then((r: any) => {
      setData(r?.data?.data);
      setLoading(false);
    });
  }, [id]);

  return (
    <Modal open fullScreen onClose={onClose}>
      {loading ? (
        <Loader />
      ) : !data ? (
        <div style={{ padding: 40, textAlign: "center", color: "#DC2626" }}>Tender not found.</div>
      ) : (
        <div style={{ padding: 24 }}>
          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{data.title}</h1>
            <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
              <StatusBadge status={data.status} />
              <Badge
                label={BIDDING_METHOD[data.biddingMethod] ?? "—"}
                color="#6B7280" bg="rgba(107,114,128,.1)"
              />
              <span style={{ fontSize: 12, color: "#94A3B8" }}>#{data.id}</span>
            </div>
          </div>

          {/* Cards grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(400px,1fr))", gap: 20 }}>

            <SectionCard icon={MdDescription} title="Tender Details" color="#3B82F6">
              <InfoRow icon={MdDescription} label="Title"       value={data.title} />
              <InfoRow icon={MdInfo}        label="Description" value={data.description} />
              {data.requirements && <InfoRow icon={MdInfo} label="Requirements" value={data.requirements} />}
              {data.closeReason  && <InfoRow icon={MdInfo} label="Close Reason"  value={data.closeReason} />}
            </SectionCard>

            <SectionCard icon={MdBusiness} title="Issuing Company" color="#7C3AED">
              <InfoRow icon={MdBusiness} label="Company"    value={data.companyName} />
              <InfoRow icon={MdPerson}   label="Created By" value={data.userFullName} />
              <InfoRow icon={MdFlight}   label="Module"     value={data.moduleName} />
              <InfoRow icon={MdInfo}     label="Module Code" value={data.moduleCode} />
            </SectionCard>

            <SectionCard icon={MdAttachMoney} title="Financials" color="#16A34A">
              <InfoRow icon={MdAttachMoney} label="Currency"       value={data.currency} />
              <InfoRow icon={MdAttachMoney} label="Min. Bid"       value={fmtMoney(data.minimumBidAmount, data.currency)} />
              <InfoRow icon={MdAttachMoney} label="Highest Bid"    value={fmtMoney(data.highestBidAmount, data.currency)} />
              <InfoRow icon={MdGavel}       label="Total Bids"     value={data.bidCount} />
              <InfoRow icon={MdGavel}       label="Bidding Method" value={BIDDING_METHOD[data.biddingMethod] ?? "—"} />
            </SectionCard>

            <SectionCard icon={MdSchedule} title="Timeline" color="#F97316">
              <InfoRow icon={MdSchedule} label="Submission Deadline" value={fmt(data.submissionDeadline)} />
              <InfoRow icon={MdSchedule} label="Created Date"        value={fmt(data.createdDate)} />
              <InfoRow icon={MdSchedule} label="Published At"        value={fmt(data.publishedAt)} />
              <InfoRow icon={MdSchedule} label="Closed At"           value={fmt(data.closedAt)} />
              {data.awardedAt && <InfoRow icon={MdGavel} label="Awarded At" value={fmt(data.awardedAt)} />}
            </SectionCard>

            {data.awardedBidId && (
              <SectionCard icon={MdGavel} title="Award" color="#7C3AED">
                <InfoRow icon={MdBusiness} label="Awarded To"     value={data.awardedCompanyName} />
                <InfoRow icon={MdSchedule} label="Awarded At"     value={fmt(data.awardedAt)} />
                <InfoRow icon={MdInfo}     label="Auto-awarded"   value={data.awardedAutomatically ? "Yes" : "No"} />
                <InfoRow icon={MdInfo}     label="Override Used"  value={data.overrideUsed ? "Yes" : "No"} />
              </SectionCard>
            )}

            {/* Module-specific details */}
            <ModuleDetailSection detail={data.moduleDetail} moduleCode={data.moduleCode} />
          </div>

          {/* Bids */}
          {(data.bids ?? []).length > 0 && (
            <div style={{ marginTop: 24 }}>
              <SectionCard icon={MdGavel} title={`Bids (${data.bids.length})`} color="#2563EB">
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#F8FAFC" }}>
                        {["#", "Company", "Submitted By", "Amount", "Status", "Rev.", "Submitted At"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #E5E7EB", color: "#64748B" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.bids.map((b: any) => {
                        const bs = BID_STATUS[b.status] ?? { label: String(b.status), color: "#6B7280" };
                        return (
                          <tr key={b.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                            <td style={{ padding: "8px 12px", color: "#94A3B8" }}>#{b.id}</td>
                            <td style={{ padding: "8px 12px", fontWeight: 600 }}>{b.companyName}</td>
                            <td style={{ padding: "8px 12px" }}>{b.userFullName}</td>
                            <td style={{ padding: "8px 12px", fontWeight: 700, color: "#16A34A" }}>{fmtMoney(b.bidAmount, b.currency)}</td>
                            <td style={{ padding: "8px 12px" }}>
                              <Badge label={bs.label} color={bs.color} bg={`${bs.color}22`} />
                            </td>
                            <td style={{ padding: "8px 12px", color: "#64748B" }}>v{b.revisionNumber}</td>
                            <td style={{ padding: "8px 12px", color: "#64748B" }}>{fmt(b.submittedAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>
          )}

          {/* Clarifications */}
          {(data.clarifications ?? []).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <SectionCard icon={MdQuestionAnswer} title={`Clarifications (${data.clarifications.length})`} color="#EAB308">
                {data.clarifications.map((c: any) => {
                  const cs = CLARIFICATION_STATUS[c.status] ?? { label: String(c.status), color: "#6B7280" };
                  return (
                    <div key={c.id} style={{ padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{c.subject}</span>
                        <Badge label={cs.label} color={cs.color} bg={`${cs.color}22`} />
                      </div>
                      <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>{c.message}</div>
                      <div style={{ fontSize: 11, color: "#94A3B8" }}>
                        From: <strong>{c.createdByUserName || "—"}</strong>
                        &nbsp;→ To: <strong>{c.targetCompanyName}</strong>
                        &nbsp;· {fmt(c.createdDate)}
                      </div>
                      {(c.responses ?? []).map((r: any) => (
                        <div key={r.id} style={{ marginTop: 8, marginLeft: 16, background: "#F8FAFC", borderRadius: 8, padding: 8, fontSize: 12 }}>
                          <strong>{r.respondedByUserName}:</strong> {r.message}
                          <span style={{ marginLeft: 8, color: "#94A3B8" }}>{fmt(r.respondedAt)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </SectionCard>
            </div>
          )}

          {/* Messages */}
          {(data.messages ?? []).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <SectionCard icon={MdMessage} title={`Messages (${data.messages.length})`} color="#6B7280">
                {data.messages.map((m: any) => (
                  <div key={m.id} style={{ padding: "8px 0", borderBottom: "1px solid #F1F5F9", fontSize: 12 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 2 }}>
                      <strong>{m.fromCompanyName}</strong>
                      <span style={{ color: "#94A3B8" }}>({m.fromUserName})</span>
                      <span style={{ color: "#94A3B8" }}>→</span>
                      <strong>{m.toCompanyName}</strong>
                      <span style={{ color: "#94A3B8" }}>({m.toUserName})</span>
                      <span style={{ marginLeft: "auto", color: "#94A3B8" }}>{fmt(m.sentAt)}</span>
                    </div>
                    <div style={{ color: "#475569" }}>{m.message}</div>
                  </div>
                ))}
              </SectionCard>
            </div>
          )}

          {/* Documents */}
          {(data.documents ?? []).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <SectionCard icon={MdPictureAsPdf} title={`Documents (${data.documents.length})`} color="#F97316">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  {data.documents.map((d: any) => (
                    <a
                      key={d.id}
                      href={d.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                        padding: 12, border: "1px solid #E5E7EB", borderRadius: 10,
                        textDecoration: "none", color: "#374151", fontSize: 11,
                        background: "#F8FAFC", width: 90,
                      }}
                    >
                      <MdPictureAsPdf size={30} color="#F97316" />
                      <span style={{ wordBreak: "break-all", textAlign: "center" }}>{d.fileName}</span>
                    </a>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const AdminTenderList = () => {
  const api = useMemo(() => createApi(), []);

  const [data, setData]       = useState<any[]>([]);
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterModule, setFilterModule] = useState<string>("");

  const [detailId, setDetailId]   = useState<number | null>(null);
  const [statusRow, setStatusRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [listRes, statsRes] = await Promise.all([
      api.adminTender.getAll(),
      api.adminTender.getStats(),
    ]);
    setData((listRes as any)?.data?.data ?? []);
    setStats((statsRes as any)?.data?.data ?? null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const moduleOptions = useMemo(() => {
    const seen = new Set<string>();
    data.forEach((r) => { if (r.moduleName) seen.add(r.moduleName); });
    return Array.from(seen).sort();
  }, [data]);

  const filtered = useMemo(() =>
    data.filter((r) => {
      if (filterStatus !== "" && String(r.status) !== filterStatus) return false;
      if (filterModule !== "" && r.moduleName !== filterModule) return false;
      return true;
    }),
    [data, filterStatus, filterModule]
  );

  const columns = useMemo(() => [
    { key: "id",       title: "#",             accessor: (r: any) => r.id },
    { key: "title",    title: "Tender Title",   accessor: (r: any) => r.title },
    { key: "module",   title: "Module",         accessor: (r: any) => r.moduleName },
    { key: "company",  title: "Company",        accessor: (r: any) => r.companyName },
    { key: "createdBy", title: "Created By",    accessor: (r: any) => r.userFullName },
    {
      key: "status",
      title: "Status",
      render: (r: any) => <StatusBadge status={r.status} />,
    },
    {
      key: "method",
      title: "Bidding",
      render: (r: any) => (
        <Badge
          label={BIDDING_METHOD[r.biddingMethod] ?? "—"}
          color={r.biddingMethod === 1 ? "#16A34A" : "#7C3AED"}
          bg={r.biddingMethod === 1 ? "rgba(22,163,74,.12)" : "rgba(124,58,237,.12)"}
        />
      ),
    },
    { key: "bids",     title: "Bids",    accessor: (r: any) => r.bidCount },
    {
      key: "highestBid",
      title: "Best Bid",
      accessor: (r: any) => fmtMoney(r.highestBidAmount, r.currency),
    },
    {
      key: "deadline",
      title: "Deadline",
      accessor: (r: any) => fmt(r.submissionDeadline),
    },
    {
      key: "createdDate",
      title: "Created",
      accessor: (r: any) => fmt(r.createdDate),
    },
    {
      key: "actions",
      title: "Actions",
      render: (r: any) => (
        <GroupButton items={[
          { id: "detail",  icon: MdVisibility, color: "#2563EB", onClick: () => setDetailId(r.id) },
          {
            id: "approve",
            icon: MdCheck,
            color: "#16A34A",
            onClick: async () => {
              if (window.confirm(`Approve tender "${r.title}"?`)) {
                await api.adminTender.approve(r.id);
                load();
              }
            },
          },
          {
            id: "reject",
            icon: MdClose,
            color: "#DC2626",
            onClick: async () => {
              if (window.confirm(`Reject tender "${r.title}"?`)) {
                await api.adminTender.reject(r.id);
                load();
              }
            },
          },
          { id: "status",  icon: MdEdit,       color: "#F59E0B", onClick: () => setStatusRow(r) },
        ]} />
      ),
    },
  ], [api, load]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Tender Management</div>
        <div style={{ fontSize: 13, color: "#64748B" }}>
          All tenders across modules — view details and manage statuses
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          <StatCard label="Total"            value={stats.totalTenders}           color="#6B7280" />
          <StatCard label="Pending Approval" value={stats.pendingApprovalTenders} color="#F59E0B" />
          <StatCard label="Active"           value={stats.activeTenders}          color="#16A34A" />
          <StatCard label="Pending Award"    value={stats.pendingAwardTenders}    color="#EAB308" />
          <StatCard label="Awarded"          value={stats.awardedTenders}         color="#7C3AED" />
          <StatCard label="Closed"           value={stats.closedTenders}          color="#F97316" />
          <StatCard label="Cancelled"        value={stats.cancelledTenders}       color="#DC2626" />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ height: 36, borderRadius: 8, border: "1px solid #D1D5DB", padding: "0 10px", fontSize: 13, minWidth: 150 }}
        >
          <option value="">All Statuses</option>
          {Object.entries(TENDER_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <select
          value={filterModule}
          onChange={(e) => setFilterModule(e.target.value)}
          style={{ height: 36, borderRadius: 8, border: "1px solid #D1D5DB", padding: "0 10px", fontSize: 13, minWidth: 180 }}
        >
          <option value="">All Modules</option>
          {moduleOptions.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>

        {(filterStatus !== "" || filterModule !== "") && (
          <Button icon={MdClose} variant="ghost" onClick={() => { setFilterStatus(""); setFilterModule(""); }}>
            Clear
          </Button>
        )}
      </div>

      <Table loading={loading} columns={columns} data={filtered} />

      {detailId !== null && (
        <TenderDetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}

      {statusRow && (
        <StatusUpdateModal
          row={statusRow}
          onClose={() => setStatusRow(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};
